import { useCallback, useState } from 'react';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui';

import { useSandboxGate } from '@backrs_ui/grow-ui/src/hooks/useSandboxGate';
import { useRequestEmulationTokenMutation } from '@backrs_ui/grow-ui/src/redux/superCoach/superCoach.api';
import { redirectToEmulation } from '@backrs_ui/grow-ui/src/utils/emulation';

interface BaseMember {
  id: string | number;
}

/**
 * Hook for handling user emulation logic.
 *
 * Supports two modes:
 * 1. Fixed member - pass `fixedMemberId` for detail views where the member is known
 * 2. Dynamic member - call `openEmulationDialog(member)` to select a member from a list
 */
export const useEmulateUser = <T extends BaseMember>(fixedMemberId?: string) => {
  const gateInSandbox = useSandboxGate();
  const [requestEmulationToken, { isLoading: isRequestingToken }] =
    useRequestEmulationTokenMutation();
  const [selectedMember, setSelectedMember] = useState<T | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For detail view: call with no args (uses fixedMemberId)
  const handleEmulateClick = useCallback(() => {
    if (gateInSandbox()) return;

    setIsDialogOpen(true);
  }, [gateInSandbox]);

  // For list view: pass member to select it, then open dialog
  const openEmulationDialog = useCallback(
    (member: T) => {
      if (gateInSandbox()) return;

      setSelectedMember(member);
      setIsDialogOpen(true);
    },
    [gateInSandbox],
  );

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedMember(null);
  }, []);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const handleConfirmEmulation = useCallback(async () => {
    const memberId = fixedMemberId ?? selectedMember?.id?.toString();

    if (!memberId || isRequestingToken) return;

    try {
      const response = await requestEmulationToken(memberId).unwrap();

      redirectToEmulation(response);
    } catch (err) {
      console.error('Token request failed:', err);
      setError(commonAppStrings.assigneeEmulation.emulationFailed);
      setIsDialogOpen(false);
    }
  }, [fixedMemberId, selectedMember, isRequestingToken, requestEmulationToken]);

  return {
    isRequestingToken,
    isDialogOpen,
    error,
    selectedMember,
    handleEmulateClick,
    openEmulationDialog,
    handleCloseDialog,
    handleConfirmEmulation,
    handleCloseError,
  };
};
